import React, { ReactNode } from 'react';
import '/node_modules/react-grid-layout/css/styles.css';
import '/node_modules/react-resizable/css/styles.css';
import { layoutEditorActions } from '@redux/store/layout-editor/layoutEditorSlice';
import {
  DeleteIcon,
  layerImageStyle,
  layerImageWrapperDivStyle,
  settingPanelStyle,
} from '@/components';
import { IconButton } from '@mui/material';
import { styled } from '@mui/system';
import { EyeOff, Pencil } from 'lucide-react';
import { useSelector } from '@redux/store/store';
import { useDispatch } from 'react-redux';
import { useLoadAnnouncementImagePreviewQuery } from '@/hooks';
import { AnnouncementElementDTO } from '@/model';

interface LayoutEditorCanvasAnnouncementItemProps {
  element: AnnouncementElementDTO;
  openDeleteModal: (id: number, name: string) => void;
  currentScale: number;
}

const LayoutEditorCanvasAnnouncementItem = ({
  element,
  openDeleteModal,
  currentScale,
}: LayoutEditorCanvasAnnouncementItemProps) => {
  const dispatch = useDispatch();

  const { editElementModalConfig, selectedLayoutId, elements, hiddenElements } =
    useSelector((state) => state.layoutEditor);

  const { data: fetchedPreview } = useLoadAnnouncementImagePreviewQuery({
    width: Number(element?.width),
    height: Number(element?.height),
    backgroundColor: element?.backgroundColor,
    imageResizeType: element?.imageResizeType,
    blackAndWhite: false,
  });

  const isSelected = element.id === selectedLayoutId;

  const onLayerSelect = () => {
    dispatch(layoutEditorActions.selectLayoutId(element.id));
  };

  const onEditClick = () =>
    dispatch(layoutEditorActions.toggleEditElementModalExpanded());

  const toggleElementVisibility = () => {
    const isVisible = !hiddenElements.has(element.id);
    dispatch(
      layoutEditorActions.setElementVisibility({
        id: element.id,
        visible: !isVisible,
      }),
    );
  };

  const onDeleteModal = () => {
    openDeleteModal(element?.id, element?.announcement?.name);
  };

  const renderSettingMenu = (): ReactNode => {
    if (!isSelected) return null;

    const inverseScale = (1 / currentScale) * 0.9;
    return (
      <div
        style={{
          ...settingPanelStyle,
          transform: `scale(${inverseScale})`,
        }}
      >
        <StyledSettingsIconButton
          color='primary'
          size='small'
          onClick={toggleElementVisibility}
        >
          <EyeOff />
        </StyledSettingsIconButton>
        {!editElementModalConfig.isExpanded ? (
          <StyledSettingsIconButton color='primary' size='small' onClick={onEditClick}>
            <Pencil />
          </StyledSettingsIconButton>
        ) : null}
        <StyledSettingsIconButton color='primary' size='small' onClick={onDeleteModal}>
          <DeleteIcon />
        </StyledSettingsIconButton>
      </div>
    );
  };

  const renderLayout = () => {
    return (
      <div
        key={element.id}
        onClick={onLayerSelect}
        style={{
          ...layerImageWrapperDivStyle(null, null),
          position: 'relative',
        }}
      >
        <img
          src={`data:image/png;base64,${fetchedPreview?.content}`}
          width={element?.width || 'auto'}
          height={element.height || 'auto'}
          alt=''
          style={layerImageStyle}
        />
      </div>
    );
  };

  return (
    <>
      {renderSettingMenu()}
      {renderLayout()}
    </>
  );
};

export default LayoutEditorCanvasAnnouncementItem;

const StyledSettingsIconButton = styled(IconButton)(() => ({
  width: '30px !important',
  height: '30px !important',
  borderRadius: '50% !important',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'rgb(231,231,231)',
    transform: 'scale(1.1)',
  },

  '&:active': {
    transform: 'scale(0.95)',
  },
}));
